"use client";

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_COLORS, PALETTE, AXIS_STYLE, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import type { NicheSnapshot } from "@/lib/types";

type Props = {
  history: NicheSnapshot[];
};

type ChartPoint = {
  date: string;
  opportunityScore: number;
  medianMrr: number;
  startupCount: number;
  marketDemand: number;
  averageGrowth30d: number;
  marketSaturation: number;
};

export function NicheHistoryChart({ history }: Props) {
  if (history.length === 0) {
    return (
      <div className="chart-card">
        <h3 className="card-title">Niche trend</h3>
        <p className="card-desc">No historical snapshots yet.</p>
        <div className="chart-placeholder">
          Run the snapshot job to populate trend history.
        </div>
      </div>
    );
  }

  const hasMarketDemand = history.some((item) => item.marketDemand > 0);
  const hasMarketGrowth = history.some((item) => item.averageGrowth30d != null);
  const hasSaturation = history.some((item) => item.marketSaturation != null);

  const data: ChartPoint[] = history.map((item) => ({
    date: item.snapshotDate,
    opportunityScore: item.opportunityScore,
    medianMrr: item.medianMrr,
    startupCount: item.startupCount,
    marketDemand: item.marketDemand,
    averageGrowth30d: item.averageGrowth30d ?? 0,
    marketSaturation: item.marketSaturation ?? 0,
  }));

  return (
    <div className="chart-card">
      <h3 className="card-title">Niche trend</h3>
      <p className="card-desc">
        Opportunity score, market growth, demand, and competition over time.
      </p>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ right: 20, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tick={AXIS_STYLE} />
            <YAxis yAxisId="left" tick={AXIS_STYLE} domain={["auto", "auto"]} />
            <YAxis yAxisId="right" orientation="right" tick={AXIS_STYLE} domain={["auto", "auto"]} />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend
              wrapperStyle={{ fontSize: "12px" }}
              formatter={(value) => <span style={{ color: "var(--text-secondary)" }}>{value}</span>}
            />
            <Line
              type="monotone"
              dataKey="opportunityScore"
              name="Opportunity Score"
              stroke={CHART_COLORS.secondary}
              strokeWidth={2}
              dot={false}
              yAxisId="left"
            />
            <Line
              type="monotone"
              dataKey="medianMrr"
              name="Median MRR"
              stroke={CHART_COLORS.success}
              strokeWidth={2}
              dot={false}
              yAxisId="right"
            />
            <Line
              type="monotone"
              dataKey="startupCount"
              name="Startup Count"
              stroke={PALETTE[5]}
              strokeWidth={2}
              dot={false}
              yAxisId="right"
            />
            {hasMarketDemand && (
              <Line
                type="monotone"
                dataKey="marketDemand"
                name="Market Demand"
                stroke={CHART_COLORS.warning}
                strokeWidth={2}
                dot={false}
                yAxisId="right"
              />
            )}
            {hasMarketGrowth && (
              <Line
                type="monotone"
                dataKey="averageGrowth30d"
                name="Market Growth %"
                stroke={PALETTE[2]}
                strokeWidth={2}
                dot={false}
                yAxisId="right"
              />
            )}
            {hasSaturation && (
              <Line
                type="monotone"
                dataKey="marketSaturation"
                name="Competition (saturation)"
                stroke="#a855f7"
                strokeWidth={2}
                dot={false}
                yAxisId="right"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
